/* eslint-disable import/extensions */
import React from 'react'
import type {
    GroupByType,
    SwingTimingData,
} from 'leaderboard/scripts/swing-path/types/globals.ts'
import type { GroupByLogic } from './utils/do-group-by-logic.ts'
import MobileLegend from './MobileLegend.tsx'
import type { PitchTypeColorMode } from './utils/can-use-pitch-type-colors.ts'
import type SwingTimingChart from './swing-timing-chart.ts'

type ChartWrapperProps = {
    rows: SwingTimingData[]
    type: GroupByType
    pitchTypeColors: boolean
    pitchTypeColorMode: PitchTypeColorMode
    hoveredKey: string | null
    handleRemove: (row: SwingTimingData) => void
    groupByLogic: GroupByLogic
    charts: SwingTimingChart[]
}

// Memoize the SVG element to prevent d3-controlled SVGs from unmounting/remounting
const ChartSVG = React.memo(() => <svg viewBox="0 0 400 500" />)

ChartSVG.displayName = 'ChartSVG'

type ChartContainerProps = ChartWrapperProps & {
    id: string
    title: string
}

// Don't memo the container - let it rerender for legend updates
// The ChartSVG component is memoized to keep the SVG DOM stable for d3
function ChartContainer({
    id,
    title,
    rows,
    type,
    pitchTypeColors,
    pitchTypeColorMode,
    hoveredKey,
    handleRemove,
    groupByLogic,
    charts,
}: ChartContainerProps) {
    return (
        <div id={id} className="swing-timing-viz-container-secondary">
            <MobileLegend
                rows={rows}
                type={type}
                pitchTypeColors={pitchTypeColors}
                pitchTypeColorMode={pitchTypeColorMode}
                hoveredKey={hoveredKey}
                handleRemove={handleRemove}
                groupByLogic={groupByLogic}
                charts={charts}
            />
            <div
                className="swing-timing-chart-title"
                style={{ display: 'flex', justifyContent: 'center' }}
            >
                <div
                    style={{
                        maxWidth: '400px',
                        width: '100%',
                    }}
                >
                    <div className="swing-timing-chart-title-double-wrapped">
                        {title}
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <ChartSVG />
            </div>
        </div>
    )
}

export default function ChartWrapper({
    rows,
    type,
    pitchTypeColors,
    pitchTypeColorMode,
    hoveredKey,
    handleRemove,
    groupByLogic,
    charts,
}: ChartWrapperProps) {
    // initSwingTimingCharts in `swing-timing-app.ts` controls everything in here.
    // The SVG elements are wrapped in React.memo to prevent them from rerendering
    // when props change, since d3 manages their content directly.
    return (
        <div style={{ position: 'relative' }}>
            <div className="swing-timing-main-title">
                Where is the bat in relation to the ball?
            </div>
            <div className="swing-timing-branding">
                <img
                    src="/site-core/images/savant-logo.svg"
                    alt="Savant"
                    className="swing-timing-branding-logo"
                />
                <div className="swing-timing-branding-title">Savant</div>
            </div>
            <div
                className="swing-timing-charts-container"
                style={{ display: 'flex' }}
            >
                <ChartContainer
                    id="viz-container-primary"
                    title="Is the batter tied up or flailing?"
                    rows={rows}
                    type={type}
                    pitchTypeColors={pitchTypeColors}
                    pitchTypeColorMode={pitchTypeColorMode}
                    hoveredKey={hoveredKey}
                    handleRemove={handleRemove}
                    groupByLogic={groupByLogic}
                    charts={charts}
                />
                <ChartContainer
                    id="viz-container-secondary-top"
                    title="Is the bat ahead or behind?"
                    rows={rows}
                    type={type}
                    pitchTypeColors={pitchTypeColors}
                    pitchTypeColorMode={pitchTypeColorMode}
                    hoveredKey={hoveredKey}
                    handleRemove={handleRemove}
                    groupByLogic={groupByLogic}
                    charts={charts}
                />
                <ChartContainer
                    id="viz-container-secondary-bottom"
                    title="Is the bat over the pitch or under?"
                    rows={rows}
                    type={type}
                    pitchTypeColors={pitchTypeColors}
                    pitchTypeColorMode={pitchTypeColorMode}
                    hoveredKey={hoveredKey}
                    handleRemove={handleRemove}
                    groupByLogic={groupByLogic}
                    charts={charts}
                />
            </div>
        </div>
    )
}
